import { createContext, useContext, useState, useEffect } from "react";
import { useAuthControllerGetUserById } from "../utils/api/kolektifOluşumAPI";
import { UserResponseDto } from "../utils/api/kolektifOluşumAPI.schemas";
import { jwtDecode } from "jwt-decode";

interface AuthContextType {
  user: UserResponseDto | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (token: string, userData: UserResponseDto) => void;
  logout: () => void;
}

interface DecodedToken {
  exp: number;
  [key: string]: any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(() =>
    localStorage.getItem("userId"),
  );

  const { data: userData } = useAuthControllerGetUserById(userId || "", {
    query: {
      enabled: !!userId,
      retry: false,
    },
  });

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem("token");
      const storedUserId = localStorage.getItem("userId");

      if (!token || !storedUserId) {
        setIsLoading(false);
        return;
      }

      try {
        const decodedToken = jwtDecode<DecodedToken>(token);
        const currentTime = Date.now() / 1000;

        if (decodedToken.exp < currentTime) {
          // Token expired, clear localStorage
          localStorage.removeItem("token");
          localStorage.removeItem("userId");
          setUserId(null);
          setUser(null);
        } else {
          // Geçerli token, userId'yi güncelle
          setUserId(storedUserId);
        }
      } catch (error) {
        // Invalid token, clear localStorage
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        setUserId(null);
        setUser(null);
      }

      setIsLoading(false);
    };

    checkTokenExpiration();

    // Check token expiration every minute
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (userData?.data) {
      setUser(userData.data);
    }
    setIsLoading(false);
  }, [userData]);

  const login = (token: string, userData: UserResponseDto) => {
    localStorage.setItem("token", token);
    localStorage.setItem("userId", userData.id);
    setUserId(userData.id);
    setUser(userData);
    setIsLoading(false);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    setUserId(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
